import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    bookDiscoveryCall,
    cancelDiscoveryCall,
    getDiscoveryCallMonthAvailability,
    getDiscoveryCallPage,
    rescheduleDiscoveryCall,
} from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import "./PortalUI.css";
import "./ReviewPage.css";

function PublicBrand() {
    return (
        <div className="review-brand">
            <picture>
                <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                <img src={logoDark} alt="Urban Podcasts" className="review-brand__logo" />
            </picture>
        </div>
    );
}

function getTokenFromUrl() {
    const searchToken = new URLSearchParams(window.location.search).get("token");
    if (searchToken) return searchToken;

    const hash = window.location.hash || "";
    const [, afterHash] = hash.split("#/");
    if (!afterHash) return null;
    const [, qs] = afterHash.split("?");
    if (!qs) return null;
    return new URLSearchParams(qs).get("token");
}

function getBooleanParamFromUrl(name) {
    const searchValue = new URLSearchParams(window.location.search).get(name);
    if (searchValue !== null) {
        return ["1", "true", "yes"].includes(String(searchValue).toLowerCase());
    }

    const hash = window.location.hash || "";
    const [, afterHash] = hash.split("#/");
    if (!afterHash) return false;
    const [, qs] = afterHash.split("?");
    if (!qs) return false;
    const hashValue = new URLSearchParams(qs).get(name);
    if (hashValue === null) return false;
    return ["1", "true", "yes"].includes(String(hashValue).toLowerCase());
}

function formatDateTime(value, timezone = "UTC") {
    if (!value) return "Not booked";
    const raw = String(value).trim();
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const text = raw.replace(" ", "T");
    const dt = hasTimezone ? new Date(text) : new Date(`${text}Z`);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
    });
}

function toYmdUtc(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function parseYmdToUtcDate(value) {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const dt = new Date(`${raw}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
}

function monthKeyFromDate(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function formatMonthLabel(dateObj) {
    return dateObj.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatSelectedDate(value) {
    const dt = parseYmdToUtcDate(value);
    if (!dt) return "";
    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function DiscoveryCallPage() {
    const token = getTokenFromUrl();
    const debugMode = getBooleanParamFromUrl("debug");
    const [date, setDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [timesLoading, setTimesLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [data, setData] = useState(null);
    const [selectedTime, setSelectedTime] = useState("");
    const [completion, setCompletion] = useState(null);
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    });
    const currentMonthStart = useMemo(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    }, []);
    const [monthAvailability, setMonthAvailability] = useState({});
    const skipNextDateLoadRef = useRef(false);
    const monthAvailabilityLoadRef = useRef(0);
    const lastLeadRef = useRef({ id: null, email: "", phone: "" });

    const normalizeField = (value) => String(value || "").trim();

    const buildBookingRedirectUrl = (baseUrl, includeParams, email, fullName) => {
        if (!baseUrl) return null;
        try {
            const url = new URL(baseUrl);
            if (includeParams) {
                const cleanEmail = normalizeField(email);
                const compactName = normalizeField(fullName).replace(/\s+/g, "");
                if (cleanEmail) url.searchParams.set("email", cleanEmail);
                if (compactName) url.searchParams.set("name", compactName);
            }
            return url.toString();
        } catch {
            return null;
        }
    };

    async function loadPage(
        dateValue,
        { preservePage = false, allowAutoPickDate = false } = {}
    ) {
        if (!token) {
            setError("Missing discovery call token in the link.");
            setLoading(false);
            setTimesLoading(false);
            return;
        }

        if (preservePage) {
            setTimesLoading(true);
        } else {
            setLoading(true);
        }
        setError("");
        try {
            const res = await getDiscoveryCallPage(token, dateValue, debugMode);
            if (!res?.ok) {
                setError(res?.message || "Failed to load booking page");
                if (!preservePage) setData(null);
            } else {
                setData(res);
                setSelectedTime("");
                const leadId = Number(res?.lead?.id || 0);
                const nextEmail = String(res?.lead?.email || "");
                const nextPhone = String(res?.lead?.phone || "");
                if (
                    !preservePage ||
                    lastLeadRef.current.id !== leadId ||
                    lastLeadRef.current.email !== nextEmail ||
                    lastLeadRef.current.phone !== nextPhone
                ) {
                    setContactEmail(nextEmail);
                    setContactPhone(nextPhone);
                    lastLeadRef.current = { id: leadId, email: nextEmail, phone: nextPhone };
                }
                if (
                    allowAutoPickDate &&
                    !dateValue &&
                    res?.selected_date &&
                    res.selected_date !== date
                ) {
                    skipNextDateLoadRef.current = true;
                    setDate(res.selected_date);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load booking page");
            if (!preservePage) setData(null);
        } finally {
            setLoading(false);
            setTimesLoading(false);
        }
    }

    useEffect(() => {
        loadPage("", { preservePage: false, allowAutoPickDate: true });
    }, [token, debugMode]);

    useEffect(() => {
        if (!date) return;
        if (skipNextDateLoadRef.current) {
            skipNextDateLoadRef.current = false;
            return;
        }
        loadPage(date, { preservePage: true, allowAutoPickDate: false });
    }, [date]);

    useEffect(() => {
        const selectedDate = parseYmdToUtcDate(date);
        if (!selectedDate) return;
        const selectedMonth = new Date(
            Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1)
        );
        setCalendarMonth((prev) =>
            monthKeyFromDate(prev) === monthKeyFromDate(selectedMonth) ? prev : selectedMonth
        );
    }, [date]);

    useEffect(() => {
        if (!token || !calendarMonth) return;
        const key = monthKeyFromDate(calendarMonth);
        const existing = monthAvailability[key];
        if (existing && !existing.loading && existing.loaded) return;

        const loadId = monthAvailabilityLoadRef.current + 1;
        monthAvailabilityLoadRef.current = loadId;
        setMonthAvailability((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), loading: true, loaded: false, dates: prev[key]?.dates || {} },
        }));

        const run = async () => {
            const res = await getDiscoveryCallMonthAvailability(token, key);
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: {
                    loading: false,
                    loaded: true,
                    dates:
                        res?.ok && res?.dates && typeof res.dates === "object"
                            ? res.dates
                            : {},
                },
            }));
        };

        run().catch(() => {
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: { loading: false, loaded: true, dates: {} },
            }));
        });
    }, [token, calendarMonth, monthAvailability]);

    const hasBooking = Boolean(data?.has_booking);
    const slots = data?.available_slots || [];
    const originalEmail = normalizeField(data?.lead?.email);
    const originalPhone = normalizeField(data?.lead?.phone);
    const currentEmail = normalizeField(contactEmail);
    const currentPhone = normalizeField(contactPhone);
    const contactChanged =
        currentEmail !== originalEmail || currentPhone !== originalPhone;
    const emailValid =
        !currentEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(currentEmail);

    const canSubmit = useMemo(
        () =>
            !saving &&
            !timesLoading &&
            emailValid &&
            (hasBooking ? Boolean(selectedTime) || contactChanged : Boolean(selectedTime)),
        [saving, timesLoading, emailValid, hasBooking, selectedTime, contactChanged]
    );
    const monthKey = monthKeyFromDate(calendarMonth);
    const canGoToPreviousMonth = calendarMonth.getTime() > currentMonthStart.getTime();
    const monthInfo = monthAvailability[monthKey] || { loading: false, loaded: false, dates: {} };
    const monthDates = monthInfo.dates || {};
    const monthFirstWeekdaySunFirst = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), 1)
    ).getUTCDay();
    const monthLastDay = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth() + 1, 0)
    ).getUTCDate();
    const calendarCells = [];
    for (let i = 0; i < monthFirstWeekdaySunFirst; i += 1) {
        calendarCells.push({ kind: "pad", key: `pad-${i}` });
    }
    for (let d = 1; d <= monthLastDay; d += 1) {
        const ymd = toYmdUtc(
            new Date(Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), d))
        );
        const available = monthDates[ymd] === true;
        const selected = ymd === date;
        calendarCells.push({
            kind: "day",
            key: ymd,
            ymd,
            day: d,
            available,
            selected,
            pending: !monthInfo.loaded && monthDates[ymd] === undefined,
        });
    }
    const selectedDateLabel = formatSelectedDate(date);
    const selectedDateTimeLabel =
        selectedDateLabel && selectedTime
            ? `${selectedDateLabel}, ${selectedTime}`
            : selectedDateLabel || "";

    async function handleBookOrReschedule() {
        if (!canSubmit) return;
        setSaving(true);
        setError("");
        setMessage("");
        setCompletion(null);

        try {
            const payload = {};
            if (selectedTime) {
                payload.date = date;
                payload.time = selectedTime;
            }
            if (currentEmail) payload.email = currentEmail;
            if (currentPhone) payload.phone = currentPhone;

            const res = hasBooking
                ? await rescheduleDiscoveryCall(token, payload)
                : await bookDiscoveryCall(token, payload);

            if (!res?.ok) {
                setError(res?.message || "Failed to save booking");
                return;
            }

            const actionType = hasBooking
                ? selectedTime
                    ? "rescheduled"
                    : "updated"
                : "booked";

            if (actionType === "booked") {
                const redirectUrl = buildBookingRedirectUrl(
                    data?.settings?.booking_redirect_url,
                    Boolean(data?.settings?.booking_redirect_include_params),
                    currentEmail || data?.lead?.email || "",
                    data?.lead?.name || ""
                );
                if (redirectUrl) {
                    window.location.assign(redirectUrl);
                    return;
                }
            }

            const confirmedAt = formatDateTime(
                res?.discovery_call_scheduled_at || data?.lead?.discovery_call_scheduled_at,
                data?.settings?.timezone || "Europe/London"
            );
            setCompletion({
                type: actionType,
                title:
                    actionType === "booked"
                        ? "Thanks for booking"
                        : actionType === "rescheduled"
                            ? "Thanks for rescheduling"
                            : "Details updated",
                summary:
                    actionType === "updated"
                        ? `Your contact details have been updated. Current booking: ${confirmedAt}.`
                        : `Your discovery call is set for ${confirmedAt}.`,
                name: data?.lead?.name || "",
            });
        } catch (err) {
            console.error(err);
            setError("Failed to save booking");
        } finally {
            setSaving(false);
        }
    }

    async function handleCancel() {
        if (!window.confirm("Cancel this discovery call?")) return;
        setSaving(true);
        setError("");
        setMessage("");
        setCompletion(null);
        try {
            const res = await cancelDiscoveryCall(token);
            if (!res?.ok) {
                setError(res?.message || "Failed to cancel booking");
                return;
            }
            setCompletion({
                type: "cancelled",
                title: "Discovery call cancelled",
                summary: "Your discovery call has been cancelled.",
                name: data?.lead?.name || "",
            });
        } catch (err) {
            console.error(err);
            setError("Failed to cancel booking");
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <p>Loading discovery call booking…</p>
                </div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">Discovery Call</h1>
                    <div className="alert alert--error">{error}</div>
                </div>
            </div>
        );
    }

    if (completion) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">{completion.title}</h1>
                    {completion.name ? (
                        <p className="review-subtitle">{completion.name}</p>
                    ) : null}
                    <p>{completion.summary}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="review-page">
            <PublicBrand />
            <div className="review-card card">
                <h1 className="review-title">Booking for Discovery Call</h1>
                <p className="review-subtitle">
                    Choose a date and 30-minute time slot.
                </p>

                {error && <div className="alert alert--error">{error}</div>}
                {message && <div className="alert alert--success">{message}</div>}

                <div className="card" style={{ marginTop: 12 }}>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Name</span>
                        <span className="card-info-row__value">{data?.lead?.name || "—"}</span>
                    </div>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Current Booking</span>
                        <span className="card-info-row__value">
                            {formatDateTime(
                                data?.lead?.discovery_call_scheduled_at,
                                data?.settings?.timezone || "UTC"
                            )}
                        </span>
                    </div>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Timezone</span>
                        <span className="card-info-row__value">
                            {data?.settings?.timezone || "Europe/London"}
                        </span>
                    </div>
                </div>

                <div className="card" style={{ marginTop: 12 }}>
                    <div className="portal-filter-bar__group" style={{ marginBottom: 10 }}>
                        <span className="portal-filter-bar__label">Email</span>
                        <input
                            type="email"
                            className="portal-filter-bar__search"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    <div className="portal-filter-bar__group">
                        <span className="portal-filter-bar__label">Phone</span>
                        <input
                            type="text"
                            className="portal-filter-bar__search"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    {!emailValid && (
                        <div className="alert alert--error" style={{ marginTop: 10 }}>
                            Please enter a valid email address.
                        </div>
                    )}
                </div>

                <div className="discovery-booking-layout">
                    <div className="discovery-booking-summary-wrap">
                        <div className="discovery-booking-summary-time">
                            {selectedDateTimeLabel || "Select date and time"}
                        </div>
                    </div>
                    <div className="discovery-booking-grid">
                        <div className="discovery-booking-col">
                            <div className="discovery-booking-col__title">Date</div>
                            <div className="discovery-booking-month-nav">
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={() => {
                                        if (!canGoToPreviousMonth) return;
                                        setCalendarMonth(
                                            new Date(
                                                Date.UTC(
                                                    calendarMonth.getUTCFullYear(),
                                                    calendarMonth.getUTCMonth() - 1,
                                                    1
                                                )
                                            )
                                        );
                                    }}
                                    disabled={saving || timesLoading || !canGoToPreviousMonth}
                                >
                                    Prev
                                </button>
                                <span className="discovery-booking-month-label">
                                    {formatMonthLabel(calendarMonth)}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={() =>
                                        setCalendarMonth(
                                            new Date(
                                                Date.UTC(
                                                    calendarMonth.getUTCFullYear(),
                                                    calendarMonth.getUTCMonth() + 1,
                                                    1
                                                )
                                            )
                                        )
                                    }
                                    disabled={saving || timesLoading}
                                >
                                    Next
                                </button>
                            </div>
                            <div className="discovery-booking-weekdays">
                                {["S", "M", "T", "W", "T", "F", "S"].map((label, idx) => (
                                    <div
                                        key={`${label}-${idx}`}
                                        className="discovery-booking-weekdays__day"
                                    >
                                        {label}
                                    </div>
                                ))}
                            </div>
                            <div className="discovery-booking-days">
                                {calendarCells.map((cell) => {
                                    if (cell.kind === "pad") return <div key={cell.key} />;
                                    const isUnavailable = !cell.available;
                                    return (
                                        <button
                                            key={cell.key}
                                            type="button"
                                            onClick={() => setDate(cell.ymd)}
                                            disabled={isUnavailable || saving || timesLoading}
                                            className={
                                                "btn btn--small discovery-booking-day " +
                                                (cell.selected ? "btn--primary" : "btn--secondary") +
                                                (isUnavailable ? " discovery-booking-day--unavailable" : "")
                                            }
                                        >
                                            {cell.day}
                                        </button>
                                    );
                                })}
                            </div>
                            {monthInfo.loading ? (
                                <p className="text-description" style={{ marginTop: 8 }}>
                                    Checking available dates...
                                </p>
                            ) : null}
                        </div>

                        <div className="discovery-booking-col">
                            <div className="discovery-booking-col__title">Time</div>
                            <div className="discovery-slots-grid">
                                {timesLoading ? (
                                    <p
                                        style={{
                                            gridColumn: "1 / -1",
                                            textAlign: "center",
                                            margin: "8px 0",
                                        }}
                                    >
                                        Loading available times…
                                    </p>
                                ) : slots.length === 0 ? (
                                    <p
                                        style={{
                                            gridColumn: "1 / -1",
                                            textAlign: "center",
                                            margin: "8px 0",
                                        }}
                                    >
                                        No time slots available for this date.
                                    </p>
                                ) : (
                                    slots.map((slot) => (
                                        <button
                                            key={slot}
                                            type="button"
                                            className={
                                                "btn discovery-slot-btn " +
                                                (selectedTime === slot
                                                    ? "btn--primary"
                                                    : "btn--secondary")
                                            }
                                            disabled={timesLoading}
                                            onClick={() => setSelectedTime(slot)}
                                        >
                                            {slot}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {debugMode && (
                    <details className="card" style={{ marginTop: 12 }}>
                        <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                            Busy-check debug
                        </summary>
                        <pre
                            style={{
                                marginTop: 10,
                                padding: 10,
                                borderRadius: 8,
                                border: "1px solid #d1d5db",
                                background: "#f9fafb",
                                overflowX: "auto",
                                fontSize: 12,
                            }}
                        >
                            {JSON.stringify(data?.debug || null, null, 2)}
                        </pre>
                    </details>
                )}

                <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                    <button
                        type="button"
                        className="btn btn--primary"
                        disabled={!canSubmit}
                        onClick={handleBookOrReschedule}
                    >
                        {saving
                            ? "Saving..."
                            : hasBooking
                                ? selectedTime
                                    ? "Reschedule Discovery Call"
                                    : "Update Details"
                                : "Book Discovery Call"}
                    </button>

                    {hasBooking && (
                        <button
                            type="button"
                            className="btn btn--danger"
                            disabled={saving}
                            onClick={handleCancel}
                        >
                            Cancel Discovery Call
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DiscoveryCallPage;
